import { useEnums, useGlobalContext } from 'Context';
import { DraftReferrals } from 'v3/Components/Pages/DraftReferrals/DraftReferrals';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { buildEndpoint, CaseStatus, useApiWorker } from 'Utilities';
import { formatDateToUtc } from 'Utilities/DateTimeUtc';
import { DropdownOption } from 'v3/Components/Form/Dropdown';
import SimpleDropdown from 'v3/Components/Form/SimpleDropdown';
import ClientHeader from './ClientHeader';
import TwoMonthComparisonChart from './TwoMonthComparisonChart';
import './index.scss';
import './TwoMonthComparisonChart/index.scss';
import { ReportsReadyForYou } from './ReportsReadyForYou';
import { guid } from '@progress/kendo-react-common';
import { InfoOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import docs from 'Assets/Icons/docs.svg';
import { set } from 'lodash';
import { dayjs } from 'Utilities/DateTimeUtc';

interface Referral {
  id: string;
  referral_date: string;
  line_of_business: string;
  claimant_name: string;
  claim_number: string;
  recent_user: string;
  status: string;
  due_date: string;
  has_alert: boolean;
}

interface Report {
  id: string;
  claimant_name: string;
  stradix_number: string;
}

interface ReferralStats {
  submitted_count: number;
  under_review_count: number;
  closed_count: number;
  total_count: number;
  due_today_count?: number;
  reopened_count?: number;
}

interface MonthlyStats {
  referralRequests: number;
  closedCases: number;
}

function useCountUp(target: number, duration = 900) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const safeTarget = Number.isFinite(target) ? target : 0;
    const start = performance.now();

    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(safeTarget * eased));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    setValue(0);
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [target, duration]);

  return value;
}

function ClientDashboard() {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [comparisonLoading, setComparisonLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('This Month');
  const history = useHistory();
  const [client, setClient] = useState<DropdownOption>();
  const [clientUrlParam, setClientUrlParam] = useState<string>('');
  const { caseStatus } = useEnums();

  const slugString = (str: string) => {
    if (!str) return '';

    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  };

  const handleClientChange = useCallback(
    (value: DropdownOption | undefined) => {
      setClient(value);
    },
    []
  );
  const [currentMonthStats, setCurrentMonthStats] = useState<MonthlyStats>({
    referralRequests: 2000,
    closedCases: 950,
  });
  const [lastMonthStats, setLastMonthStats] = useState<MonthlyStats>({
    referralRequests: 1200,
    closedCases: 900,
  });

  function navigateToAllClaimantsPage() {
    const clientId = client?.value ? `clientId=${client.value}` : '';
    history.push(`/Referrals/AllClaimants${clientId ? '?' + clientId : ''}`);
  }

  const getMTDDateRange = () => {
    const from = formatDateToUtc(dayjs().startOf('month').toDate(), true);
    const to = formatDateToUtc(dayjs().toDate(), true);
    return `${from}|${to}`;
  };

  const navigateToSubmittedMTD = () => {
    history.push(`/Referrals/OpenReferrals?referralDate=${getMTDDateRange()}${clientUrlParam}`);
  };

  const navigateToOpenCasesCount = () => {
    history.push(`/Referrals/OpenReferrals?${clientUrlParam}`);
  };

  const navigateToClosedCasesMTD = () => {
    const closedStatus = Object.values(caseStatus).find(
      (item) => item.displayName === CaseStatus.Closed
    )
    const clientIdParam = client?.value ? `&clientId=${client.value}` : '';
    history.push(`/Referrals/ClosedReferrals?caseStatus=${closedStatus?.value || ''}&closedDate=${getMTDDateRange()}${clientUrlParam}`);
  };

  const navigateToReopenedCount = () => {
    const reopened = Object.values(caseStatus).find(
      (item) => item.displayName === CaseStatus.ReOpened
    );
    history.push(
      `/Referrals/OpenReferrals?caseStatus=${reopened?.value || ''}${clientUrlParam}`
    );
  };

  const API = useApiWorker();
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  useEffect(() => {
    // Simulate initial loading for skeleton animation
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const GetNewReferralsEndpoint = buildEndpoint(
    ENDPOINTS.dashboard.endpointString('GetClosedOpenAndSubmittedReferrals')
  );

  const GetTwoMonthComparisonEndpoint = buildEndpoint(
    ENDPOINTS.dashboard.endpointString('two-month-comparison')
  );

  useEffect(() => {
    const fetchData = async () => {
      setStatsLoading(true);
      const start = new Date();
      start.setUTCDate(1);
      start.setUTCHours(0, 0, 0, 0);
      const end = new Date();
      end.setUTCHours(23, 59, 59, 999);
      const emptyClientId = '00000000-0000-0000-0000-000000000000';

      // Get user's timezone from browser
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      try {
        const { data } = await API.post(
          GetNewReferralsEndpoint,
          {
            TimeFrame: timeframe,
            clientId: client?.id ?? emptyClientId,
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            timeZone: userTimeZone,
          },
          {
            responseType: 'json',
          }
        );
        setStats((prev) => ({
          ...prev,
          submitted_count: data.submittedReferrals || 0,
          under_review_count: data.openReferrals || 0,
          closed_count: data.closedReferrals || 0,
          total_count:
            (data.submittedReferrals || 0) +
            (data.openReferrals || 0) +
            (data.closedReferrals || 0),
          due_today_count: data.dueTodayReferrals || 0,
          reopened_count: data?.reOpenedReferrals || 0,
        }));
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    if (client?.id) {
      setClientUrlParam(
        `&clientName=${slugString(String(client?.displayName ?? ''))}&clientId=${client?.id}`
      );
    } else {
      setClientUrlParam('');
    }

    fetchData();
  }, [timeframe, loading, client]);

  // Fetch two-month comparison data
  useEffect(() => {
    const fetchTwoMonthComparison = async () => {
      setComparisonLoading(true);
      const emptyClientId = '00000000-0000-0000-0000-000000000000';

      // Get user's timezone from browser
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      try {
        const { data } = await API.get(
          `${GetTwoMonthComparisonEndpoint}?clientId=${
            client?.id ?? emptyClientId
          }&timeZone=${encodeURIComponent(userTimeZone)}`
        );

        setCurrentMonthStats({
          referralRequests: data.currentMonth?.referralRequests || 0,
          closedCases: data.currentMonth?.closedCases || 0,
        });

        setLastMonthStats({
          referralRequests: data.lastMonth?.referralRequests || 0,
          closedCases: data.lastMonth?.closedCases || 0,
        });
      } catch (error) {
        console.error('Error fetching two-month comparison:', error);
      } finally {
        setComparisonLoading(false);
      }
    };

    if (!loading) {
      fetchTwoMonthComparison();
    }
  }, [client, loading]);

  const submittedCount = useCountUp(stats?.submitted_count || 0);
  const openCount = useCountUp(stats?.under_review_count || 0);
  const closedCount = useCountUp(stats?.closed_count || 0);
  const reopenedCount = useCountUp(stats?.reopened_count ?? 0);

  if (loading) {
    return (
      <div
        className="dashboard"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <div style={{ color: '#6b7280' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <ClientHeader client={client} setClient={handleClientChange} />
      <main className="main">
        <div className="stats-section">
          <div className="card">
            <div className="section-header">
              <h2>{!userdata?.isInternal ? 'Your Cases' : 'Your Referrals'}</h2>
              <span className="due-today-text">
                <img className="docs-icon" src={docs} alt="Docs Icon" />
                <span>{stats?.due_today_count || 0} cases due today</span>
              </span>
              <span
                className="view-all-link"
                onClick={navigateToAllClaimantsPage}
              >
                View All
              </span>
            </div>
            <div
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingLeft: 32,
              }}
            >
              <div className="stats-grid">
                <div className="stats-card blue">
                  {statsLoading ? (
                    <>
                      <div className="stats-card-value">
                        <div className="skeleton skeleton-value"></div>
                      </div>
                      <div className="stats-card-label">
                        <div className="skeleton skeleton-label"></div>
                      </div>
                      <div className="stats-card-label">
                        <div className="skeleton skeleton-label-small"></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="stats-card-value">{submittedCount}</div>
                      <div className="stats-card-label">Submitted Requests</div>
                      <div className="stats-card-label">MTD</div>
                      <button className="stats-card-link">
                        View
                        <ArrowRight size={16} />
                      </button>
                    </>
                  )}
                </div>

                <div className="stats-card blue-green">
                  {statsLoading ? (
                    <>
                      <div className="stats-card-value">
                        <div className="skeleton skeleton-value"></div>
                      </div>
                      <div className="stats-card-label">
                        <div className="skeleton skeleton-label"></div>
                      </div>
                      <div className="stats-card-label">
                        <div className="skeleton skeleton-label-small"></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="stats-card-value">
                        <span>{openCount}</span>
                        <Tooltip title="Total of Open and Re-Opened cases">
                          <InfoOutlined
                            style={{
                              color: '#9ca3af',
                              marginLeft: '0.5rem',
                              width: '16px',
                              height: '16px',
                            }}
                          />
                        </Tooltip>
                      </div>
                      <div className="stats-card-label">Open Cases</div>
                      <div className="stats-card-label">Count</div>
                      <button
                        className="stats-card-link"
                        onClick={navigateToOpenCasesCount}
                      >
                        View
                        <ArrowRight size={16} />
                      </button>
                    </>
                  )}
                </div>
                <div className="stats-card teal">
                  {statsLoading ? (
                    <>
                      <div className="stats-card-value">
                        <div className="skeleton skeleton-value"></div>
                      </div>
                      <div className="stats-card-label">
                        <div className="skeleton skeleton-label"></div>
                      </div>
                      <div className="stats-card-label">
                        <div className="skeleton skeleton-label-small"></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="stats-card-value">{reopenedCount}</div>
                      <div className="stats-card-label">Re-Opened Cases</div>
                      <div className="stats-card-label">Count</div>
                      <button
                        className="stats-card-link"
                        onClick={navigateToReopenedCount}
                      >
                        View
                        <ArrowRight size={16} />
                      </button>
                    </>
                  )}
                </div>
                <div className="stats-card orange">
                  {statsLoading ? (
                    <>
                      <div className="stats-card-value">
                        <div className="skeleton skeleton-value"></div>
                      </div>
                      <div className="stats-card-label">
                        <div className="skeleton skeleton-label"></div>
                      </div>
                      <div className="stats-card-label">
                        <div className="skeleton skeleton-label-small"></div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="stats-card-value">{closedCount}</div>
                      <div className="stats-card-label">Closed Cases</div>
                      <div className="stats-card-label">MTD</div>
                      <button
                        className="stats-card-link"
                        onClick={navigateToClosedCasesMTD}
                      >
                        View
                        <ArrowRight size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <TwoMonthComparisonChart
                currentMonth={currentMonthStats}
                lastMonth={lastMonthStats}
                loading={comparisonLoading}
              />
            </div>
          </div>
          <ReportsReadyForYou
            clientId={client?.id as string}
            clientName={slugString(String(client?.displayName ?? ''))}
          />
        </div>
        <DraftReferrals
          header={false}
          id={client?.id as string}
          clientId={client?.id as string}
          disableUrlSync
        />
      </main>
    </div>
  );
}

export default ClientDashboard;
